import { ReactNode, useEffect, useState } from 'react'
import {
    Box,
    ButtonBase,
    CssBaseline,
    Divider,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
    Menu,
    MenuItem
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router'
import { USER_NAME_KEY } from '@/core/constants/auth'
import { clearAuthData } from '@/core/hooks/useStorage'

import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import HistoryIcon from '@mui/icons-material/History'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import LogoutIcon from '@mui/icons-material/Logout'

type HamburgerMenuItem = {
    label: string
    value: string
    href: string
    icon: ReactNode
}

type HamburgerLayoutProps = {
    menuItems?: HamburgerMenuItem[]
    activeMenu?: string
    onMenuSelect?: (menuValue: string) => void
    children?: ReactNode
}

const drawerWidth = 340
const collapsedWidth = 100

const createHamburgerIcon = (color: string) => (
    <Box
        component="span"
        sx={{
            width: 20,
            height: 2,
            borderRadius: 999,
            backgroundColor: color,
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: -6,
                width: '100%',
                height: '100%',
                left: 0,
                borderRadius: 999,
                backgroundColor: color
            },
            '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                bottom: -6,
                width: '100%',
                height: '100%',
                borderRadius: 999,
                backgroundColor: color
            }
        }}
    />
)

const defaultMenuItems: HamburgerMenuItem[] = [
    { label: 'Nhà phân phối', value: 'distributor', href: '/mlm/distributor', icon: <PersonSearchIcon /> },
    { label: 'Lịch sử đơn hàng', value: 'orderhistory', href: '/mlm/orderhistory', icon: <HistoryIcon /> },
    { label: 'Hoa hồng', value: 'commission', href: '/mlm/commission', icon: <MonetizationOnIcon /> },
    { label: 'Phả hệ', value: 'genealogy', href: '/mlm/genealogy', icon: <AccountTreeIcon /> },
    { label: 'Đăng xuất', value: 'logout', href: '', icon: <LogoutIcon /> }
]

const HamburgerLayout = ({ menuItems = defaultMenuItems, activeMenu, onMenuSelect, children }: HamburgerLayoutProps) => {
    const theme = useTheme()
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
    const navigate = useNavigate()
    const [openMenu, setOpenMenu] = useState(isDesktop)
    const [selectedMenu, setSelectedMenu] = useState('')
    const [userInitial, setUserInitial] = useState('')
    const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
    const isUserMenuOpen = Boolean(userMenuAnchor)

    const pathname = useLocation().pathname.split('/').pop()

    useEffect(() => {
        if (activeMenu) {
            setSelectedMenu(activeMenu)
        } else {
            const menuItem = defaultMenuItems.find(item => item.value === pathname)
            if (menuItem) {
                setSelectedMenu(menuItem.value)
            }
        }
    }, [activeMenu, menuItems])

    useEffect(() => {
        setOpenMenu(isDesktop)
    }, [isDesktop])

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        const updateInitial = () => {
            const username = window.localStorage.getItem(USER_NAME_KEY) ?? ''
            const initial = username.trim().charAt(0).toUpperCase()
            setUserInitial(initial || '')
        }

        updateInitial()

        const handleStorage = (event: StorageEvent) => {
            if (event.key === USER_NAME_KEY) {
                updateInitial()
            }
        }

        window.addEventListener('storage', handleStorage)

        return () => {
            window.removeEventListener('storage', handleStorage)
        }
    }, [])

    const handleMenuSelect = (item: HamburgerMenuItem) => {
        setSelectedMenu(item.value)
        onMenuSelect?.(item.value)

        if (item.value === 'logout') {
            handleLogout()
            return
        }

        if (item.href) {
            navigate(item.href, { replace: false })
        }

        if (!isDesktop) {
            setOpenMenu(false)
        }
    }

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setUserMenuAnchor(event.currentTarget)
    }

    const handleCloseUserMenu = () => {
        setUserMenuAnchor(null)
    }

    const handleLogout = () => {
        clearAuthData()
        handleCloseUserMenu()
        navigate('/', { replace: true })
    }

    const currentDrawerWidth = openMenu ? drawerWidth : collapsedWidth

    const drawerContent = (
        <Stack sx={{ height: '100%', color: 'rgba(255,255,255,0.9)', overflow: 'hidden' }}>
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <IconHamburger openMenu={openMenu} setOpenMenu={setOpenMenu} />
            </Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mx: '30px' }} />

            <List disablePadding sx={{ px: 2, pt: 2 }}>
                {menuItems.map(item => (
                    <ListItem key={item.value} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                            className={'list-button'}
                            onClick={() => handleMenuSelect(item)}
                            selected={selectedMenu === item.value}
                            title={!openMenu ? item.label : undefined}
                            sx={{
                                borderRadius: 2,
                                boxShadow: selectedMenu === item.value ? '0px 7px 10px 9px rgb(0 0 0 / 13%)' : 'none',
                                color: 'inherit',
                                justifyContent: 'flex-start',
                                px: 2,
                                minHeight: 48,
                                fontSize: 16,
                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(255,255,255,0.12)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.12)'
                                    }
                                },
                                '&:not(.Mui-selected):hover': {
                                    backgroundColor: 'rgba(255,255,255,0.08)'
                                }
                            }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    // boxShadow: selectedMenu ? '1px 5px 17px 0px rgb(40 40 40 / 71%)' : 'none',
                                    display: {
                                        lg: 'flex',
                                        xs: openMenu ? 'flex' : 'none'
                                    },
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mr: '20px',
                                    '& svg': { fontSize: 20 },
                                }}>
                                {item.icon}
                            </Box>
                            {openMenu && <ListItemText primary={item.label} sx={{ whiteSpace: 'nowrap' }} />}
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Stack>
    )

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f6fa' }}>
            <CssBaseline />

            <Box
                component="header"
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: { lg: `${currentDrawerWidth}px`, xs: openMenu ? `${currentDrawerWidth}px` : 0 },
                    right: 0,
                    zIndex: theme.zIndex.drawer + 1,
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    transition: theme.transitions.create('left', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.shorter
                    })
                }}>
                <Toolbar sx={{ minHeight: 72, justifyContent: { xs: 'space-between', lg: 'flex-end' }, px: { xs: 2, md: 4 } }}>
                    <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                        <IconHamburger openMenu={openMenu} setOpenMenu={setOpenMenu} />
                    </Box>
                    <ButtonBase
                        onClick={handleOpenUserMenu}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: '#e3f2fd',
                            color: '#1f2430',
                            fontWeight: 700
                        }}>
                        {userInitial}
                    </ButtonBase>
                    <Menu
                        anchorEl={userMenuAnchor}
                        open={isUserMenuOpen}
                        onClose={handleCloseUserMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        onClick={handleCloseUserMenu}>
                        <MenuItem onClick={handleLogout}>Đăng xuất</MenuItem>
                    </Menu>
                </Toolbar>
            </Box>

            <Box
                component="nav"
                aria-label="menu navigation"
                sx={{
                    width: { lg: `${currentDrawerWidth}px`, xs: openMenu ? `${currentDrawerWidth}px` : 0 },
                    flexShrink: 0,
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.shorter
                    })
                }}>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'block', lg: 'block' },
                        '& .MuiDrawer-paper': {
                            width: { xs: openMenu ? `${currentDrawerWidth}px` : 0, lg: `${currentDrawerWidth}px` },
                            boxSizing: 'border-box',
                            backgroundColor: { xs: openMenu ? '#1f2430' : 'transparent', lg: '#1f2430' },
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.shorter
                            }),
                            overflowX: 'hidden'
                        }
                    }}>
                    {drawerContent}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.shorter
                    })
                }}>
                <Toolbar sx={{ minHeight: 72 }} />
                <Box sx={{ p: { xs: 2, md: 4 } }}>{children}</Box>
            </Box>
        </Box>
    )
}

const IconHamburger = ({ openMenu, setOpenMenu }: { openMenu: boolean; setOpenMenu: (open: boolean) => void }) => {
    return (
        <Stack direction="row" alignItems="center" justifyContent="flex-start" sx={{ p: { xs: 0, lg: '30px' }, gap: '20px', minHeight: 72 }}>
            <ButtonBase
                onClick={() => setOpenMenu(!openMenu)}
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e74c3c',
                    flexShrink: 0
                }}>
                {createHamburgerIcon('#FFFFFF')}
            </ButtonBase>
            {openMenu && (
                <Typography variant="h6" fontSize={16} fontWeight={700} color="common.white" noWrap>
                    Unicity Marketing Việt Nam
                </Typography>
            )}
        </Stack>
    )
}

export default HamburgerLayout
